import React from 'react';
import { Link } from 'react-router-dom';
import { Check, Sparkles, Phone } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const Services = () => {
  const packs = [
    {
      name: 'Pack Clean',
      price: '40€',
      color: 'blue',
      features: [
        'Nettoyage des plastiques',
        'Aspiration complète',
        'Nettoyage des vitres',
        'Parfum habitacle'
      ]
    },
    {
      name: 'Pack Extérieur',
      price: '30€',
      color: 'blue',
      features: [
        'Nettoyage de la carrosserie',
        'Nettoyage des jantes',
        'Nettoyage des panneaux de porte',
        'Application d\'un produit lustrant'
      ]
    },
    {
      name: 'Pack Moteur',
      price: '30€',
      color: 'blue',
      features: [
        'Nettoyage compartiment moteur au pinceau'
      ]
    },
    {
      name: 'Pack Prestige',
      price: '70€',
      color: 'blue',
      features: [
        'Pack Clean',
        'Shampoinage des tissus (sièges, tapis, moquette, coffre) ou Nettoyage des sièges en cuir + produit nourissant'
      ]
    },
    {
      name: 'Pack Gold',
      price: '70€',
      color: 'gold',
      features: [
        'Pack Clean',
        'Pack Extérieur'
      ]
    },
    {
      name: 'Pack VIP',
      price: '90€',
      color: 'gold',
      features: [
        'Pack Prestige',
        'Pack Extérieur'
      ]
    },
    {
      name: 'Pack Detailing',
      price: '160€',
      color: 'gold',
      popular: true,
      features: [
        'Pack VIP',
        'Nettoyage approfondi en détail'
      ]
    }
  ];

  const extras = [
    { name: 'Poils d\'animaux', price: '+20€' },
    { name: 'Sable', price: '+20€' },
    { name: 'Shampoinage plafonnier', price: '+20€' },
    { name: 'Fiente de pigeon', price: '+10€' },
    { name: 'Retrait d\'autocollant', price: '+10€' },
    { name: 'Véhicule 7 places', price: '+20€' }
  ];

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header Section */}
      <section className="py-20 px-4 bg-gradient-to-b from-gray-950 to-black">
        <div className="container mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 animate-fade-in">
            Nos <span className="text-blue-500">Formules</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto animate-slide-up">
            Choisissez la formule adaptée à vos besoins. Prestations professionnelles pour un résultat impeccable.
          </p>
        </div>
      </section>

      {/* Packs Section */}
      <section className="py-16 px-4 bg-black">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {packs.map((pack, index) => (
              <Card 
                key={index}
                className={`relative bg-gray-900/50 p-8 hover:bg-gray-900/70 transition-all duration-300 hover:scale-105 ${
                  pack.color === 'blue' 
                    ? 'border-blue-500/50 hover:border-blue-500' 
                    : 'border-yellow-500/50 hover:border-yellow-500'
                }`}
              >
                {pack.popular && (
                  <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-yellow-500 text-black font-bold px-4 py-1">
                    Le plus complet
                  </Badge>
                )}
                
                <div className="text-center mb-6">
                  <h3 className={`text-2xl font-bold mb-2 ${
                    pack.color === 'blue' ? 'text-blue-400' : 'text-yellow-400'
                  }`}>
                    {pack.name}
                  </h3>
                  <div className="text-4xl font-bold text-white">{pack.price}</div>
                </div>
                
                <ul className="space-y-3 mb-8">
                  {pack.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start text-gray-300">
                      <Check className={`h-5 w-5 mr-2 flex-shrink-0 mt-0.5 ${
                        pack.color === 'blue' ? 'text-blue-500' : 'text-yellow-500'
                      }`} />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <a href="tel:0766845519" className="block">
                  <Button className={`w-full ${
                    pack.color === 'blue' 
                      ? 'bg-blue-600 hover:bg-blue-700' 
                      : 'bg-yellow-600 hover:bg-yellow-700'
                  } text-white transition-all duration-300 hover:shadow-lg`}>
                    <Phone className="mr-2 h-4 w-4" />
                    Réserver
                  </Button>
                </a>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Extras Section */}
      <section className="py-16 px-4 bg-gradient-to-b from-black to-gray-950">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="text-blue-500">Extras</span>
            </h2>
            <p className="text-lg text-gray-300">Options supplémentaires pour un service personnalisé</p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            {extras.map((extra, index) => (
              <Card 
                key={index}
                className="bg-gray-900/50 border-blue-500/30 p-6 hover:bg-gray-900/70 transition-all duration-300 hover:scale-105"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Sparkles className="h-5 w-5 text-blue-500 mr-3" />
                    <span className="text-white font-medium">{extra.name}</span>
                  </div>
                  <span className="text-blue-400 font-bold text-lg">{extra.price}</span>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gray-950">
        <div className="container mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Prêt à redonner vie à votre véhicule ?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Réservez dès maintenant votre rendez-vous et profitez d'un service de qualité professionnelle.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:0766845519">
              <Button className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-blue-500/50">
                <Phone className="mr-2 h-5 w-5" />
                07 66 84 55 19
              </Button>
            </a>
            <Link to="/contact">
              <Button className="bg-white hover:bg-gray-100 text-black px-8 py-6 text-lg rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-xl">
                Nous contacter
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;